// Unpair a station's PC, and remove a station (ADR-0057): the two confirms, as small centred dialogs, shared by the
// Stations list and the floor's station window. Each says what happens before anything is sent, shows the server's
// refusal in the dialog (a session still running, no PC paired) and sends one Idempotency-Key per confirm, so a retry of
// the same confirm acts once. `stations:manage` is enforced again by the server.

import { ApiProblemError } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { Notice } from '../ui';
import { useRemoveStation, useUnpairStation } from './queries';

/** The station a confirm is about: enough to name it and to send the request. */
export interface RemovalTarget {
  readonly kind: 'unpair' | 'remove';
  readonly organizationId: string;
  readonly venueId: string;
  readonly stationId: string;
  readonly number: number;
  readonly name: string | null;
  /** Whether a PC is paired to it now (Remove says the PC goes back to its pairing screen). */
  readonly paired: boolean;
}

export function StationRemovalDialog({
  target,
  onClose,
  onRemoved,
}: {
  readonly target: RemovalTarget | null;
  readonly onClose: () => void;
  /** After a successful remove (the station window closes). */
  readonly onRemoved?: () => void;
}): ReactNode {
  return (
    <Dialog open={target !== null} onOpenChange={(open) => (open ? undefined : onClose())} size="sm" title={<Title target={target} />}>
      {/* Remounted per open, so a new confirm has its own Idempotency-Key and no leftover error. */}
      {target === null ? null : <Confirm key={`${target.kind}:${target.stationId}`} target={target} onClose={onClose} {...(onRemoved === undefined ? {} : { onRemoved })} />}
    </Dialog>
  );
}

function labelOf(target: RemovalTarget, t: ReturnType<typeof useTranslation>['t']): string {
  return target.name ?? t('floor.tile.numbered', { number: target.number });
}

function Title({ target }: { readonly target: RemovalTarget | null }): ReactNode {
  const { t } = useTranslation('console');
  if (target === null) return '';
  return t(`stations.removal.${target.kind}.title`, { station: labelOf(target, t) });
}

function Confirm({ target, onClose, onRemoved }: { readonly target: RemovalTarget; readonly onClose: () => void; readonly onRemoved?: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const unpair = useUnpairStation(target.organizationId, target.venueId);
  const remove = useRemoveStation(target.organizationId, target.venueId);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [error, setError] = useState<string | null>(null);
  const station = labelOf(target, t);
  const removing = target.kind === 'remove';
  const pending = removing ? remove.isPending : unpair.isPending;

  const refusal = (failure: unknown): string => {
    if (problemCode(failure) === PROBLEM.conflict) {
      // The api answers 409 for a running session and, on unpair, for a station with no PC paired.
      const detail = failure instanceof ApiProblemError ? (failure.problem.detail ?? '') : '';
      if (!removing && /\bpaired\b|\bno pc\b/i.test(detail) && !/session/i.test(detail)) return t('stations.removal.unpair.notPaired', { station });
      return t(`stations.removal.${target.kind}.running`, { station });
    }
    return t(`stations.removal.${target.kind}.failed`);
  };

  const confirm = () => {
    setError(null);
    const vars = { stationId: target.stationId, idempotencyKey };
    const handlers = {
      onSuccess: () => {
        show({ tone: 'ok', title: t(`stations.removal.${target.kind}.done`, { station }) });
        onClose();
        if (removing) onRemoved?.();
      },
      onError: (failure: unknown) => setError(refusal(failure)),
    };
    if (removing) remove.mutate(vars, handlers);
    else unpair.mutate(vars, handlers);
  };

  return (
    <div className="console-form">
      {error === null ? null : (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}
      {removing ? (
        <>
          <p>{t('stations.removal.remove.body', { station })}</p>
          {target.paired ? <p>{t('stations.removal.remove.pcGoesBack')}</p> : null}
        </>
      ) : (
        <>
          <p>{t('stations.removal.unpair.body')}</p>
          <p className="console-muted">{t('stations.removal.unpair.keeps', { station })}</p>
        </>
      )}
      <div className="console-form__actions console-form__actions--end">
        <Button variant="ghost" onClick={onClose} disabled={pending}>
          {t('common.cancel')}
        </Button>
        <Button variant="danger" className="clav-button--danger-solid" busy={pending} onClick={confirm}>
          {t(`stations.removal.${target.kind}.confirm`)}
        </Button>
      </div>
    </div>
  );
}
